import {fabric} from 'fabric';
import {useCallback, useContext} from 'react';
import type {Filters} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/actions/UyoControl/ImageFilters/types';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';

type Canvas = {
  // biome-ignore lint/suspicious/noExplicitAny: existing error
  getActiveObject: () => any;
  // biome-ignore lint/suspicious/noExplicitAny: existing error
  fire: (eventName: string, activeObject: any) => void;
};

// Grayscale, Sepia, Brownie, Vintage, Kodachrome, technicolor, polaroid
export const IMAGE_FILTERS = {
  none: {
    // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
    f: () => {},
    label: 'None',
    cssFilter: {filter: ''},
  },
  grayscale: {
    f: () => new fabric.Image.filters.Grayscale(),
    label: 'B&W',
    cssFilter: {filter: 'grayscale(1)'},
  },
  sepia: {
    f: () => new fabric.Image.filters.Sepia(),
    label: 'Sepia',
    cssFilter: {filter: 'sepia(1)'},
  },
  brownie: {
    // @ts-expect-error fabric doesn't have a type for this class
    f: () => new fabric.Image.filters.Brownie(),
    label: 'Earthy',
    cssFilter: {filter: 'sepia(80%) contrast(90%) brightness(80%) saturate(280%)'},
  },
  vintage: {
    // @ts-expect-error fabric doesn't have a type for this class
    f: () => new fabric.Image.filters.Vintage(),
    label: 'Vintage',
    cssFilter: {
      filter: 'sepia(10%) contrast(85%) brightness(90%) saturate(290%) hue-rotate(-10deg)',
    },
  },
  kodachrome: {
    // @ts-expect-error fabric doesn't have a type for this class
    f: () => new fabric.Image.filters.Kodachrome(),
    label: 'Chrome',
    cssFilter: {filter: 'contrast(90%) saturate(120%) sepia(0%) hue-rotate(-35deg)'},
  },
  technicolor: {
    // @ts-expect-error fabric doesn't have a type for this class
    f: () => new fabric.Image.filters.Technicolor(),
    label: 'Techno',
    cssFilter: {filter: 'contrast(130%) saturate(150%) brightness(110%) hue-rotate(-10deg)'},
  },
  polaroid: {
    // @ts-expect-error fabric doesn't have types for this
    f: () => new fabric.Image.filters.Polaroid(),
    label: 'Polaroid',
    cssFilter: {
      filter: 'contrast(90%) saturate(120%) brightness(105%) sepia(10%) hue-rotate(-75deg)',
    },
  },
};

export const useImageFilter = () => {
  const {
    canvas,
    setProcessing,
  }: {
    canvas: Canvas | null;
    processing: boolean;
    setProcessing: (newState: boolean) => void;
  } = useContext(EventCreateContext);

  const applyImageFilter = useCallback(
    (filterName: string) => {
      if (!canvas) return;

      setProcessing(true);
      const activeObject = (canvas as Canvas).getActiveObject();
      if (filterName !== 'none') {
        const filter = IMAGE_FILTERS[filterName as Filters];
        // documentation said to filter.push; but we only want one filter at a time
        activeObject.filters = [filter.f()];
      } else {
        activeObject.filters = [];
      }

      activeObject.applyFilters();
      (canvas as Canvas).fire('object:modified', activeObject);

      // Add a small delay to ensure the loading state is visible
      setTimeout(() => {
        setProcessing(false);
      }, 1000);
    },
    [canvas, setProcessing]
  );

  return {
    applyImageFilter,
  };
};
